
import { isAppUsing } from '@@/bits/flip'
import { __, n__ } from 'bits/intl'
import { Component, Prop, Vue } from 'bits/vue'
import ImageThumbnail from 'library/v4/components/ImageThumbnail.vue'
import OzBadge, { OzBadgeColorScheme, OzBadgeSizePreset } from '@@/library/v4/components/OzBadge.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzListRow, { OzListRowSeparator } from 'library/v4/components/OzListRow.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from 'library/v4/components/OzPlainButton.vue'
import RelativeTime from 'library/v4/components/relative-time.vue'
import type { Library, WallCamelCase as Wall, Wall as WallSnakeCase } from 'types'

@Component({ components: { OzListRow, OzPlainButton, ImageThumbnail, RelativeTime, OzIcon, OzBadge } })
export default class OzWallListRow extends Vue {
  OzPlainButtonColorScheme = OzPlainButtonColorScheme
  OzPlainButtonSizePreset = OzPlainButtonSizePreset
  OzBadgeColorScheme = OzBadgeColorScheme
  OzBadgeSizePreset = OzBadgeSizePreset
  isAppUsing = isAppUsing

  /**
   * Dark mode
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'

  /**
   * Invert colors
   */
  @Prop({ default: false }) invertColors!: boolean

  /**
   * Wall to display
   */
  @Prop({ required: true }) wall!: Wall | WallSnakeCase

  /**
   * The timestamp type to show.
   */
  @Prop({ default: 'updatedAt' }) dateAttribute!: string

  /**
   * Set this to false to use normal cursor, tabindex -1 etc
   */
  @Prop({ default: true }) clickable!: boolean

  /**
   * Show the more button
   */
  @Prop({ default: false }) xMore!: boolean

  /**
   * Whether there is a library associated with the wall
   */
  @Prop({ required: false }) library!: Library

  /**
   * Separator type
   */
  @Prop({ default: OzListRowSeparator.Short }) separator!: OzListRowSeparator

  get title(): string {
    return this.wall.title || __('Untitled padlet')
  }

  get dominantColorAsRgb(): string {
    return (
      (this.wall as Wall).thumbnail.dominantColorAsRgb || (this.wall as WallSnakeCase).thumbnail.dominant_color_as_rgb
    )
  }

  get date(): string {
    if (this.dateAttribute === 'updatedAt') {
      return (this.wall as Wall).updatedAt || (this.wall as WallSnakeCase).updated_at
    } else {
      return this.wall[this.dateAttribute]
    }
  }

  get accountDisplayName(): string {
    const displayName =
      'displayName' in this.wall.builder ? this.wall.builder.displayName : this.wall.builder.display_name
    return this.library ? this.library.name : displayName
  }

  get daysTillWallIsDeleted(): number {
    const daysLeft =
      'daysTillDeletion' in this.wall ? this.wall.daysTillDeletion : (this.wall as WallSnakeCase).days_till_deletion
    return daysLeft ?? 0
  }

  get daysTillDeletedDisplay(): string {
    return n__('%{num} day left', '%{num} days left', this.daysTillWallIsDeleted, { num: this.daysTillWallIsDeleted })
  }

  get wallDeletingSoon(): boolean {
    return this.daysTillWallIsDeleted <= 3
  }

  get xRelativeTimestamp(): boolean {
    return this.dateAttribute !== 'trashedAt'
  }

  get xTemplate(): boolean {
    if (!isAppUsing('templates')) return false
    if ('isTemplate' in this.wall) {
      return this.wall.isTemplate === true
    }
    return false
  }

  select(): void {
    /**
     * Select the wall.
     */
    this.$emit('select', this.wall)
  }

  more(): void {
    /**
     * View more wall actions.
     */
    this.$emit('more', this.wall)
  }
}

export { OzListRowSeparator }
